/**
 * The one rule for a tool call that succeeded but needs review (the yellow
 * "warning" state). Used by every place that marks tool results: the shared
 * agent loop, the Claude Code stream parser, and the dashboard when it rebuilds
 * a conversation from history — so live and reloaded turns always agree.
 *
 * Replaced three drifting copies (MA-356): the dashboard's ignored Claude
 * Code's `mcp__atlas__` names, all three looked for a boundary message nothing
 * emits, and all three matched `"ok":false` against pretty-printed JSON
 * (`"ok": false`), so failing quality checks were never flagged.
 */

const MCP_PREFIX = 'mcp__atlas__';

/** Quality tools return JSON whose top-level `ok` is false when the check fails. */
const QUALITY_TOOLS = new Set(['code_check', 'type_check', 'js_check', 'test_run', 'quality_check']);

/**
 * Bash resolves (does not throw) when the command fails or is stopped; the
 * first line says so. Labels from `src/agent/tools/builtins/shell.ts`.
 */
const BASH_PROBLEM = /^(Exit code \d|Timed out after|Terminated by signal)/;

/** The tool's own name, without the Claude Code MCP prefix. */
function bareName(toolName: string): string {
  return toolName.startsWith(MCP_PREFIX) ? toolName.slice(MCP_PREFIX.length) : toolName;
}

/** Top-level `ok === false` in a quality-tool result (tolerates a truncated tail). */
function qualityFailed(result: string): boolean {
  try {
    const parsed: unknown = JSON.parse(result);
    return typeof parsed === 'object' && parsed !== null && (parsed as { ok?: unknown }).ok === false;
  } catch {
    // A size cap can cut the JSON; `ok` is the first key of every quality result.
    return /^\s*\{\s*"ok":\s*false\b/.test(result);
  }
}

/** True when a successful tool result should be shown as a warning. */
export function isToolWarning(toolName: string, result: string): boolean {
  const name = bareName(toolName);
  if (QUALITY_TOOLS.has(name)) return qualityFailed(result);
  if (name === 'bash') return BASH_PROBLEM.test(result);
  return false;
}
